import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import ArrowDownwardRoundedIcon from '@mui/icons-material/ArrowDownwardRounded';
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import DiamondRoundedIcon from "@mui/icons-material/DiamondRounded";

/**
 * Returns { colorClass, borderClass, bgClass } based on subscription status.
 *  - cancelled → amber
 *  - expired   → red
 *  - active / other → green (primary)
 */
function getStatusColors(status) {
  const normalized = (status || "").toLowerCase().trim();

  if (normalized === "cancelled") {
    return {
      colorClass: "text-[var(--color-accent-amber)]",
      borderClass: "border-[rgba(255,168,0,0.25)]",
      bgClass: "bg-[rgba(255,168,0,0.12)]",
    };
  }

  if (normalized === "expired") {
    return {
      colorClass: "text-[var(--color-accent-red)]",
      borderClass: "border-[rgba(255,69,0,0.25)]",
      bgClass: "bg-[rgba(255,69,0,0.12)]",
    };
  }

  // active / default → green (primary)
  return {
    colorClass: "text-[var(--color-primary)]",
    borderClass: "border-[rgba(200,255,0,0.25)]",
    bgClass: "bg-[rgba(200,255,0,0.12)]",
  };
}

export default function SubscriptionPanel({ subscription }) {
  if (!subscription) {
    return (
      <section className="mt-6">
        <p className="text-[var(--color-text-muted)] xl:text-sm text-xs font-bold uppercase tracking-[0.08em] mb-4">
          Subscription Overview
        </p>
        <div className="relative bg-[var(--color-surface)] border border-[var(--color-border)] rounded-[var(--radius-md)] flex flex-col items-center justify-center text-center p-8 min-h-[220px]">
          <div className="w-14 h-14 rounded-[var(--radius-md)] inline-flex items-center justify-center shrink-0 bg-[rgba(200,255,0,0.12)] text-[var(--color-primary)] border border-[rgba(200,255,0,0.25)] mb-4">
            <DiamondRoundedIcon sx={{ fontSize: 28 }} />
          </div>
          <h3 className="m-0 text-[var(--color-text)] xl:text-xl md:text-lg text-base font-bold mb-1">
            No Subscription History Found
          </h3>
          <p className="m-0 text-[var(--color-text-muted)] xl:text-base md:text-sm text-xs leading-[1.5]">
            You do not have an active subscription or any past subscription history available.
          </p>
        </div>
      </section>
    );
  }

  const { colorClass, borderClass, bgClass } = getStatusColors(subscription.status);

  return (
    <section className="mt-6">
      <p className="text-[var(--color-text-muted)] xl:text-sm text-xs font-bold uppercase tracking-[0.08em] mb-4">
        Subscription Overview
      </p>
      <div className="relative bg-[var(--color-surface)] border border-[var(--color-border)] rounded-[var(--radius-md)] p-4">
        <div className="flex justify-between gap-4 md:items-center md:flex-row flex-col items-start">
          <div className="flex items-center gap-4">
            <div className={`w-14 h-14 rounded-[var(--radius-md)] inline-flex items-center justify-center shrink-0 ${bgClass} ${colorClass} border ${borderClass}`}>
              <DiamondRoundedIcon sx={{ fontSize: 28 }} />
            </div>
            <div className="flex flex-col gap-3">
              <h2 className="xl:text-[22px] md:text-xl text-lg">{subscription.planName}</h2>
              <span className={`inline-flex items-center justify-center min-h-[34px] rounded-full px-3 ${bgClass} ${colorClass} border ${borderClass} xl:text-base md:text-sm text-xs font-bold capitalize`}>{subscription.status}</span>
            </div>
          </div>

          <div className={`xl:text-[22px] md:text-xl text-lg font-extrabold ${colorClass} md:w-auto w-full md:text-right text-left tracking-[-0.03em]`}>{subscription.price}</div>
        </div>

        <div className="h-[1px] bg-[var(--color-border)] my-6" />

        <div className="grid md:grid-cols-[minmax(0,1fr)_48px_minmax(0,1fr)] grid-cols-1 gap-4 items-center">
          <div>
            <p className="text-[var(--color-text-muted)] xl:text-base md:text-sm text-xs font-medium mb-2">Start Date</p>
            <p className="text-[var(--color-text)] xl:text-[22px] md:text-xl text-lg font-bold tracking-[-0.03em]">{subscription.startDate}</p>
          </div>

          <div className="md:flex hidden justify-center text-[var(--color-text-muted)]">
            <ArrowForwardRoundedIcon />
          </div>
          <div className="flex md:hidden justify-start text-[var(--color-text-muted)]">
            <ArrowDownwardRoundedIcon />
          </div>

          <div>
            <p className="text-[var(--color-text-muted)] xl:text-base md:text-sm text-xs font-medium mb-2">Expiry Date</p>
            <p className="text-[var(--color-text)] xl:text-[22px] md:text-xl text-lg font-bold tracking-[-0.03em]">{subscription.expiryDate}</p>
          </div>
        </div>

        <div className="h-[1px] bg-[var(--color-border)] my-6" />

        <div>
          <p className="text-[var(--color-text-muted)] text-xs font-bold tracking-[0.14em] uppercase mb-5">Plan Features</p>
          <div className="grid gap-4">
            {subscription.features.map((feature) => (
              <div className="flex items-center gap-4 xl:text-xl md:text-lg text-base font-medium" key={feature}>
                <CheckCircleRoundedIcon
                  sx={{ color: "var(--color-primary)", fontSize: { xl: '22px', md: '20px', xs: '18px'} }}
                />
                <span>{feature}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
